<script setup lang="ts">
import type { DSComment } from '@/types'

const props = defineProps({
  comment: {
    type: Object as PropType<DSComment>,
    required: true,
  },
  modelType: {
    type: String,
    required: true,
  },
  modelId: {
    type: String,
    required: true,
  },
  isReply: {
    type: Boolean,
    default: false,
  },
  isHighlighted: {
    type: Boolean,
    default: false,
  },
  isScrollTarget: {
    type: Boolean,
    default: false,
  },
})
const emits = defineEmits(['toggleInput'])
const { isUserLoggedIn } = storeToRefs(useUserStore())
const { addLikedComment } = useUserBase()
const { checkLikedComment } = useLike()
const { openLoginHint } = useLoginHint()

const { toDateString } = useDateHelper()

const { pressLike } = useCommentBase(
  props.modelType,
  props.modelId,
)

const ifReplyVisible = ref<boolean>(false)
const {
  authorId,
  author: authorName,
  authorAvatar,
  created,
  comment: commentContent,
  id,
  parentId,
} = props.comment

const liked = ref<boolean>(false)

const commentParagraph = computed(() => {
  return commentContent.replace(/\r\n|\r|\n/g, '<br>')
})

function showReplyInput() {
  if (!isUserLoggedIn.value) {
    openLoginHint()
    return
  }
  emits('toggleInput', id)
}

function submitLike(commentId: string) {
  pressLike(commentId)
  addLikedComment(commentId, authorId)
  liked.value = true
}

onMounted(async () => {
  // if (props.isScrollTarget) {
  //   if (replyCount) ifReplyVisible.value = true
  //   const el = getCurrentInstance().ctx.$refs[`comment_${id}`]
  //   const top = el.offsetTop / 1.1
  //   window.scrollTo({
  //     top,
  //     behavior: 'smooth',
  //   })
  // }
  if (id) {
    const likeRecord = await checkLikedComment(id)
    liked.value = !!likeRecord
  }
})
</script>

<template>
  <div
    :ref="`comment_${id}`" class="flex flex-wrap pl-2"
    :class="{ 'bg-neutral-50 rounded-md': isHighlighted, 'p-4': !isReply }"
  >
    <div class="mr-2 rounded-full w-[32px] h-[32px] bg-neutral-300">
      <common-avatar
        :src="authorAvatar"
        :alt="authorName"
        :fallback="authorName[0]"
      />
    </div>
    <div class="flex-1 w-full">
      <div class="flex items-center mb-2 justify-between flex-row">
        <p class="font-semibold text-sm text-neutral-500">
          {{ authorName || '親愛的陌生人' }}
        </p>
        <ui-button
          v-check-login="() => submitLike(id!)"
          class="flex flex-row items-center h-auto p-0"
          variant="ghost"
        >
          <i v-if="comment.likeCount > 0" i-ph-heart-fill class="text-xl" :style="`color: ${liked ? '#EF4444' : '#525252'};`" />
          <i v-else i-ph-heart class="text-xl" style="color: #A3A3A3;" />
          <p class="ml-1 text-neutral-600">
            {{ comment.likeCount }}
          </p>
        </ui-button>
      </div>
      <span class="text-sm pt-2 text-neutral-900 leading-5" v-html="commentParagraph" />
      <div class="flex flex-row items-center pt-2 text-sm">
        <p class="text-neutral-500 text-xs font-light">
          {{ toDateString(created) }}
        </p>
        <span v-if="!parentId" class="px-1">・</span>
        <ui-button
          v-if="!parentId" class="text-neutral-900 p-0 h-auto"
          variant="link"
          @click="showReplyInput"
        >
          <span>回覆</span>
        </ui-button>
        <span v-if="!parentId && comment.replyCount > 0" class="px-1">・</span>
        <ui-button
          v-if="!parentId && comment.replyCount > 0"
          class="text-neutral-900 p-0 h-auto"
          variant="link"
          @click="ifReplyVisible = true"
        >
          <span>{{ comment.replyCount }} 則回覆</span>
        </ui-button>
      </div>
    </div>

    <div v-if="ifReplyVisible" class="w-full mt-4 flex-auto pl-8">
      <common-comment-post
        v-for="reply in comment.replyList"
        :key="reply.id"
        :is-reply="true"
        :comment="reply"
        :model-type="modelType"
        :model-id="modelId"
      />
    </div>
  </div>
  <hr v-if="!isReply" class="block border-neutral-300 my-2">
</template>
